import {
    Combobox,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxClear,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxList,
    ComboboxSeparator,
    ComboboxTrigger,
    useComboboxAnchor,
} from 'components/ui/combobox';
import { InputGroupButton } from 'components/ui/input-group';
import React, { useEffect, useMemo, useState } from 'react';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations.ts';
import { getSelectorKey } from '../../getSelectorKey.ts';
import {
    isEmptinessOption,
    MainFilterForm,
    MultiselectFilterForm,
    NOT_NULL_FILTER_SENTINEL,
    NULL_FILTER_SENTINEL,
    QuickFilterForm,
    SelectOption,
} from '../../ListTableTypes.tsx';
import { MIN_SEARCH_LENGTH_FOR_SELECT_ALL } from '../inputConstants.ts';
import {
    checkIfSomeFilteredOptionNotSelected,
    checkIfSomeFilteredOptionSelected,
    mergeOptions,
    separateOptions,
} from '../utils.ts';
import { FilterLabel } from './FilterLabel';
import {
    CheckboxOptionRow,
    CollapsedTags,
    ExcludeToggle,
    isSelectAllOption,
    keepTagsOnBackspace,
    pickBesideEmptiness,
    SELECT_ALL_OPTION,
} from './filterSelectParts';
import { useOptionsInactiveMaxRows } from './hooks/useOptionsInactiveMaxRows.ts';

const getOptionLabel = (option: SelectOption) => `${option.label}`;

export const Multiselect: React.FC<{
    filter: MultiselectFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: SelectOption[], groupSelector: string) => void;
    onExcludingChange?: (isExcluding: boolean, groupSelector: string) => void;
}> = React.memo(({ filter, onChange, onExcludingChange, quickFilters, mainFilters }) => {
    const optionsWithNull = useMemo<SelectOption[]>(() => {
        const base = filter?.options || [];
        const pinned = [
            ...(filter?.nullOption ? [{ value: NULL_FILTER_SENTINEL, label: filter.nullOption.label }] : []),
            ...(filter?.notEmptyOption
                ? [{ value: NOT_NULL_FILTER_SENTINEL, label: filter.notEmptyOption.label }]
                : []),
        ];
        return [...pinned, ...base];
    }, [filter?.options, filter?.nullOption, filter?.notEmptyOption]);
    const [sortedOptions, setSortedOptions] = useState<SelectOption[]>(optionsWithNull);
    const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(filter?.selected || []);
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const [isExcluding, setIsExcluding] = useState(filter?.isExcluding || false);
    const anchorRef = useComboboxAnchor();

    useEffect(() => {
        // Keep sortedOptions in sync when async-loaded options or the null option change.
        if (
            sortedOptions.length !== optionsWithNull.length ||
            (optionsWithNull.length > 0 &&
                sortedOptions[0]?.value !== optionsWithNull[0]?.value &&
                !sortedOptions.length)
        ) {
            setSortedOptions(optionsWithNull);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsWithNull]);

    // The same match the list used to apply: the label contains what was typed, ignoring case and
    // accents, and the typed text is not trimmed.
    const filteredOptions = useMemo(() => {
        if (!inputValue) return sortedOptions;
        const needle = foldForSearch(inputValue);
        return sortedOptions.filter((option) => foldForSearch(getOptionLabel(option)).includes(needle));
    }, [inputValue, sortedOptions]);

    const isAllFilteredSelected = !checkIfSomeFilteredOptionNotSelected(selectedOptions, filteredOptions);
    const isAllFilteredNotSelected = !checkIfSomeFilteredOptionSelected(selectedOptions, filteredOptions);
    const isSelectAllShown = inputValue.length >= MIN_SEARCH_LENGTH_FOR_SELECT_ALL && filteredOptions.length > 0;
    const items = isSelectAllShown ? [SELECT_ALL_OPTION, ...filteredOptions] : filteredOptions;

    const handleClearOptions = () => setSelectedOptions([]);
    const handleToggleSelectAll = () => {
        if (filteredOptions.length === sortedOptions.length) {
            const options = selectedOptions.length === sortedOptions.length ? [] : sortedOptions;
            setSelectedOptions(options);
            return options;
        }
        const options = isAllFilteredSelected
            ? separateOptions(selectedOptions, filteredOptions)
            : mergeOptions(selectedOptions, filteredOptions);
        setSelectedOptions(options);
        return options;
    };

    // monitor for "clear all filters" essentially
    useEffect(() => {
        if (filter?.selected.length === 0 && selectedOptions.length > 0) {
            handleClearOptions();
        }
    }, [filter?.selected, selectedOptions]);

    // sync isExcluding state with filter prop
    useEffect(() => {
        if (filter?.isExcluding !== isExcluding) {
            setIsExcluding(filter?.isExcluding || false);
        }
    }, [filter?.isExcluding, isExcluding]);

    const handleExcludingToggle = (checked: boolean) => {
        setIsExcluding(checked);
        if (onExcludingChange) {
            onExcludingChange(checked, getSelectorKey(filter));
        }
    };

    const commit = (options: SelectOption[]) => {
        setSelectedOptions(options);
        onChange(options, getSelectorKey(filter));
    };

    const handleValueChange = (next: SelectOption[]) => {
        if (next.some(isSelectAllOption)) {
            onChange(handleToggleSelectAll(), getSelectorKey(filter));
            return;
        }
        if (filter.notEmptyOption) {
            const { options, dropsExclusion } = pickBesideEmptiness(selectedOptions, next);
            if (dropsExclusion && isExcluding) handleExcludingToggle(false);
            commit(options);
            return;
        }
        commit(next);
    };

    // Opening the list brings the selected options to the top, the rest keeping their configured
    // order (orderNum) below them.
    const handleOnOpen = () => {
        const baseOptions = sortedOptions.length ? sortedOptions : optionsWithNull;
        if (!baseOptions.length) return;
        setSortedOptions(
            baseOptions.toSorted((a, b) => {
                // The pinned pair stays at the head, above its divider.
                if (filter.notEmptyOption && (isEmptinessOption(a) || isEmptinessOption(b))) {
                    return Number(isEmptinessOption(b)) - Number(isEmptinessOption(a));
                }
                // By value: a selection restored from the URL or saved filters holds copies, not these objects.
                const aSelected = selectedOptions.some((selected) => selected.value === a.value);
                const bSelected = selectedOptions.some((selected) => selected.value === b.value);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                const aIndex = optionsWithNull.findIndex((opt) => opt.value === a.value);
                const bIndex = optionsWithNull.findIndex((opt) => opt.value === b.value);
                return aIndex - bIndex;
            })
        );
    };

    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && filter.selected.length > 0) {
            handleClearOptions();
            onChange([], getSelectorKey(filter));
        }
    }, [filter, isHidden, onChange]);

    const { focused, setFocused, hiddenCount, visibleCount, measureRef } = useOptionsInactiveMaxRows({
        selectedOptions,
    });

    if (isHidden) {
        return null;
    }

    return (
        <div className="flex grow flex-col gap-2" key={`filter-${filter.selector}`}>
            {filter.label && (
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues}>
                    {filter.canExclude && (
                        <ExcludeToggle
                            checked={isExcluding}
                            onChange={handleExcludingToggle}
                            disabled={!!filter.notEmptyOption && selectedOptions.some(isEmptinessOption)}
                            tooltip={filter.controlTooltips?.exclude}
                        />
                    )}
                </FilterLabel>
            )}
            <Combobox<SelectOption, true>
                multiple
                items={items}
                value={selectedOptions}
                // The list is filtered here, so the select-all row can lead whatever the search left.
                filter={null}
                itemToStringLabel={getOptionLabel}
                isItemEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                inputValue={inputValue}
                // Only typing changes the search: picking an option keeps it, so several matches can be
                // picked in turn.
                onInputValueChange={(value, details) => {
                    if (details.reason === 'input-change' || details.reason === 'input-clear') setInputValue(value);
                }}
                open={open}
                onOpenChange={(next, details) => {
                    // Picking an option — the select-all row included — keeps the list open.
                    if (!next && details.reason === 'item-press') return;
                    setOpen(next);
                    if (next) handleOnOpen();
                    else setInputValue('');
                }}
                onValueChange={handleValueChange}
            >
                <ComboboxChips
                    ref={anchorRef}
                    className="bg-paper relative pr-14 has-data-[slot=combobox-chip]:pr-14 has-data-[slot=combobox-chip]:pl-2.5"
                >
                    <CollapsedTags
                        comboboxChips
                        values={selectedOptions}
                        getLabel={(option) => getOptionLabel(option as SelectOption)}
                        onDelete={(index) => commit(selectedOptions.filter((_, position) => position !== index))}
                        showAll={focused}
                        visibleCount={visibleCount}
                        hiddenCount={hiddenCount}
                        measureRef={measureRef}
                    />
                    <ComboboxChipsInput
                        id={`multi-autocomplete-${filter.selector}`}
                        aria-label={filter.label}
                        placeholder={t('search')}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onKeyDown={keepTagsOnBackspace}
                    />
                    {/* Pinned to the field's right edge, clear of the tags, as the list's own buttons were. */}
                    <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
                        <ComboboxClear className="size-5" />
                        <InputGroupButton
                            size="icon-xs"
                            variant="ghost"
                            render={<ComboboxTrigger />}
                            tabIndex={-1}
                            className="size-5 data-pressed:bg-transparent"
                        />
                    </div>
                </ComboboxChips>
                <ComboboxContent anchor={anchorRef} className="shadow-dropdown">
                    <ComboboxEmpty>{t('no-options')}</ComboboxEmpty>
                    <ComboboxList>
                        {(option: SelectOption) =>
                            isSelectAllOption(option) ? (
                                <CheckboxOptionRow
                                    key={String(option.value)}
                                    option={option}
                                    checked={isAllFilteredSelected}
                                    indeterminate={!isAllFilteredSelected && !isAllFilteredNotSelected}
                                >
                                    {getOptionLabel(option)}
                                </CheckboxOptionRow>
                            ) : (
                                <React.Fragment key={String(option.value)}>
                                    <CheckboxOptionRow
                                        option={option}
                                        checked={selectedOptions.some((selected) => selected.value === option.value)}
                                    >
                                        {getOptionLabel(option)}
                                    </CheckboxOptionRow>
                                    {option.value === NOT_NULL_FILTER_SENTINEL ? <ComboboxSeparator /> : null}
                                </React.Fragment>
                            )
                        }
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
        </div>
    );
});
